import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import {
  TrendingUp, Calendar, DollarSign, Filter, ChevronLeft, ChevronRight,
  Trash2, Repeat
} from 'lucide-react';
import '../styles/Admin.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const Income = () => {
  const [stats, setStats] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ current: 1, pages: 1 });
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [filterMonth, setFilterMonth] = useState('');
  const [filterType, setFilterType] = useState(''); // '', 'monthly', 'yearly'

  useEffect(() => { fetchStats(); fetchPayments(); }, [filterYear, filterMonth, filterType]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('mstechs_token');
      const response = await fetch(`${API_URL}/api/payments/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) setStats(data.stats);
    } catch (err) { console.error(err); }
  };

  const fetchPayments = async (page = 1) => {
    try {
      const token = localStorage.getItem('mstechs_token');
      let url = `${API_URL}/api/payments?page=${page}&limit=15`;
      if (filterYear) url += `&year=${filterYear}`;
      if (filterMonth) url += `&month=${filterMonth}`;
      if (filterType) url += `&type=${filterType}`;
      const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await response.json();
      if (data.success) {
        setPayments(data.payments);
        setPagination(data.pagination);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Obrisati ovu uplatu? Datum naplate ce biti vracen unazad.')) return;
    try {
      const token = localStorage.getItem('mstechs_token');
      const response = await fetch(`${API_URL}/api/payments/${paymentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        fetchPayments(pagination.current);
        fetchStats();
      } else {
        alert(data.message);
      }
    } catch (err) { console.error(err); }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('sr-RS').format(amount) + ' RSD';
  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('sr-RS');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Avg', 'Sep', 'Okt', 'Nov', 'Dec'];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="chart-tooltip">
          <p className="tooltip-label">{label}</p>
          <p className="tooltip-value">{formatCurrency(payload[0].value)}</p>
        </div>
      );
    }
    return null;
  };

  if (loading) return <div className="admin-loading"><div className="admin-spinner"></div></div>;

  const chartData = stats?.last12Months?.map(item => ({ name: item.monthName, prihod: item.total })) || [];
  const yearlyChartData = stats?.yearlyData?.map(item => ({ name: monthNames[item.month - 1], prihod: item.total })) || [];

  return (
    <div className="income-page">
      <div className="income-summary">
        <div className="summary-card">
          <div className="summary-icon green"><DollarSign /></div>
          <div className="summary-info">
            <span className="summary-label">Ovaj mesec</span>
            <span className="summary-value">{formatCurrency(stats?.monthlyIncome?.total || 0)}</span>
            <span className="summary-count">{stats?.monthlyIncome?.count || 0} uplata</span>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon blue"><TrendingUp /></div>
          <div className="summary-info">
            <span className="summary-label">Ukupno ({filterYear})</span>
            <span className="summary-value">{formatCurrency(stats?.yearlyData?.reduce((sum, m) => sum + m.total, 0) || 0)}</span>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon purple"><Calendar /></div>
          <div className="summary-info">
            <span className="summary-label">Svo vreme</span>
            <span className="summary-value">{formatCurrency(stats?.totalIncome?.total || 0)}</span>
            <span className="summary-count">{stats?.totalIncome?.count || 0} uplata</span>
          </div>
        </div>
      </div>

      <div className="charts-grid">
        <div className="chart-card">
          <div className="chart-header"><h3>Poslednjih 12 meseci</h3></div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a1f3a" />
                <XAxis dataKey="name" stroke="#8892b0" tick={{ fill: '#8892b0' }} />
                <YAxis stroke="#8892b0" tick={{ fill: '#8892b0' }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="prihod" stroke="#00d9ff" strokeWidth={3} dot={{ fill: '#00d9ff', r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-header">
            <h3>Mesecni prihod - {filterYear}</h3>
            <div className="year-selector">
              <button onClick={() => setFilterYear(filterYear - 1)}><ChevronLeft size={20} /></button>
              <span>{filterYear}</span>
              <button onClick={() => setFilterYear(filterYear + 1)} disabled={filterYear >= new Date().getFullYear()}><ChevronRight size={20} /></button>
            </div>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={yearlyChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a1f3a" />
                <XAxis dataKey="name" stroke="#8892b0" tick={{ fill: '#8892b0' }} />
                <YAxis stroke="#8892b0" tick={{ fill: '#8892b0' }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="prihod" fill="url(#colorGradient)" radius={[4, 4, 0, 0]} />
                <defs>
                  <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00d9ff" stopOpacity={1}/>
                    <stop offset="100%" stopColor="#7b2ff7" stopOpacity={0.8}/>
                  </linearGradient>
                </defs>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="payments-history">
        <div className="history-header">
          <h3><DollarSign size={20} /> Istorija uplata</h3>
          <div className="history-filters">
            <div className="filter-group">
              <Filter size={16} />
              <select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)}>
                <option value="">Svi meseci</option>
                {monthNames.map((name, i) => <option key={i} value={i + 1}>{name}</option>)}
              </select>
            </div>
            <div className="filter-group">
              <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                <option value="">Svi tipovi</option>
                <option value="monthly">Mesecne</option>
                <option value="yearly">Godisnje</option>
              </select>
            </div>
          </div>
        </div>

        <div className="table-container">
          <table className="payments-table">
            <thead>
              <tr>
                <th>Datum</th>
                <th>Klijent</th>
                <th>Tip</th>
                <th>Period</th>
                <th>Iznos</th>
                <th>Akcije</th>
              </tr>
            </thead>
            <tbody>
              {payments.length > 0 ? payments.map((p) => (
                <tr key={p._id}>
                  <td>{formatDate(p.paymentDate)}</td>
                  <td>{p.clientName}</td>
                  <td>
                    <span className={`type-badge ${p.type}`}>
                      {p.type === 'monthly' ? <><Repeat size={11} /> Mesecna</> : <><Calendar size={11} /> Godisnja</>}
                    </span>
                  </td>
                  <td>{formatDate(p.periodStart)} - {formatDate(p.periodEnd)}</td>
                  <td className="amount-cell">{formatCurrency(p.amount)}</td>
                  <td>
                    {p.canDelete ? (
                      <button className="btn-icon delete" onClick={() => handleDeletePayment(p._id)}>
                        <Trash2 size={16} />
                      </button>
                    ) : (
                      <span className="btn-icon-disabled" title="Postoji novija uplata istog tipa">—</span>
                    )}
                  </td>
                </tr>
              )) : (
                <tr><td colSpan="6" className="empty-table"><p>Nema uplata</p></td></tr>
              )}
            </tbody>
          </table>
        </div>

        {pagination.pages > 1 && (
          <div className="pagination">
            <button onClick={() => fetchPayments(pagination.current - 1)} disabled={pagination.current === 1} className="pagination-btn">
              <ChevronLeft size={18} />
            </button>
            <span className="pagination-info">Strana {pagination.current} od {pagination.pages}</span>
            <button onClick={() => fetchPayments(pagination.current + 1)} disabled={pagination.current === pagination.pages} className="pagination-btn">
              <ChevronRight size={18} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Income;